"use client";

import React from "react";
import { UseFormReturn, useFieldArray, useWatch } from "react-hook-form";
import { z as zod } from "zod";

import {
	Box,
	Button,
	CardContent,
	Chip,
	InputAdornment,
	Stack,
	Typography,
	boxClasses,
	Link,
} from "@mui/material";

import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import { Field, schemaHelper } from "@onair/minimal-ui/hook-form";
import { useResponsive } from "@onair/minimal-ui/hooks/use-responsive";
import { Iconify } from "@onair/minimal-ui/iconify";
import { ExtraImageSvg } from "@onair/ui/svg/extra-image";
import InputLabelWizard from "./input-label-wizard.tsx";
import { WizardSchemaType } from "./index.tsx";
import PreviewInfo from "./preview/info.tsx";

import { CategoryType, UserRole } from "@onair/repositories/rest-api.ts";
import { RHFSelectCategoriesV2 } from "@onair/ui/form-controls/select-categories-v2";
import { isFacebookVideo, isYoutubeVideo } from "@onair/utils/check-url";
import CardWizard from "./card-wizard.tsx";
import { SUGGEST_BANNER_LINK } from "@onair/constants";
// import { RHFSelectSurvey } from "#src/components/form-controls/select-survey";
import { RHFSelectSurvey } from "@onair/ui/form-controls/select-survey";

import { useAuthStore } from "@onair/store/auth";

interface PropTypes {
	methods: UseFormReturn<WizardSchemaType>;
	navigator?: React.ReactNode;
}

export const StepInfoSchema = zod
	.object({
		name: zod
			.string({ message: "Vui lòng nhập tên sự kiện!" })
			.min(1, { message: "Vui lòng nhập tên sự kiện!" })
			.max(100, { message: "Vui lòng nhập tối đa 100 ký tự" }),
		categories: zod
			.array(
				zod.object({
					value: zod.string(),
					label: zod.string(),
				}),
			)
			.nonempty({ message: "Vui lòng chọn lĩnh vực!" })
			.max(3, { message: "Vui lòng chọn tối đa 3 lĩnh vực!" }),
		hashtags: zod
			.array(zod.string())
			.max(10, "Vui lòng nhập tối đa 10 hashtags"),
		content: schemaHelper.editor({
			message: { required_error: "Vui lòng nhập nội dung của sự kiện" },
		}),
		banner: schemaHelper.file({
			message: { required_error: "Vui lòng tải lên ảnh bìa" },
		}),
		photos: zod
			.array(
				zod.object({
					file: zod.custom<File | string | null>(),
				}),
			)
			.optional(),
		videos: zod
			.array(
				zod.object({
					url: zod.string(),
				}),
			)
			.optional(),
		surveyIds: zod.array(zod.string()).optional(),
		surveyRequired: zod
			.record(zod.string(), zod.boolean().optional())
			.optional(),
		_recalculatePhotosAndVideos: zod.number(),
	})
	.superRefine((data, ctx) => {
		data.videos?.forEach((el, index) => {
			if (el.url && !isYoutubeVideo(el.url) && !isFacebookVideo(el.url)) {
				ctx.addIssue({
					code: zod.ZodIssueCode.custom,
					message: "URL không hợp lệ, chỉ hỗ trợ Youtube, Facebook",
					path: ["videos", index, "url"],
				});
			}
		});
	});

const StepInfo: React.FC<PropTypes> = ({ methods, navigator }) => {
	const currentUser = useAuthStore();

	const { setValue, control } = methods;

	const mdDown = useResponsive("down", "md");

	const photos = useWatch({
		name: "stepInfo.photos",
		control,
	});

	const recalculatePhotosAndVideos = useWatch({
		name: "stepInfo._recalculatePhotosAndVideos",
		control,
	});

	const { fields, append, remove } = useFieldArray({
		control,
		name: "stepInfo.videos",
	});

	const {
		fields: photoFields,
		append: appendPhoto,
		remove: removePhoto,
	} = useFieldArray({
		control,
		name: "stepInfo.photos",
	});

	React.useEffect(() => {
		const hasEmptyPhoto = (photos || []).some((photo) => !photo.file);
		if (!hasEmptyPhoto) {
			appendPhoto({ file: null });
		}
	}, [photos]);

	const data = useWatch({
		control,
	});

	const shouldGetSurveysFromProduct = currentUser.role !== UserRole.HOST;

	const pickSurveyFromProductId = shouldGetSurveysFromProduct
		? data.productId
		: undefined;

	return (
		<Stack direction="row" spacing={3}>
			<Box flex={1}>
				<Stack
					sx={{
						gap: {
							xs: 3,
							md: 2,
						},
					}}
				>
					<CardWizard>
						<CardContent>
							<Stack spacing={1}>
								<InputLabelWizard required>Tên sự kiện</InputLabelWizard>
								<Field.Text
									name="stepInfo.name"
									placeholder="Nhập tên sự kiện"
								/>
								<Typography
									variant="caption"
									color="text.secondary"
									align="right"
								>
									Tối đa <strong>100</strong> ký tự.
								</Typography>
							</Stack>
						</CardContent>
					</CardWizard>

					<CardWizard>
						<CardContent>
							<Stack spacing={3}>
								<Box>
									<Stack spacing={0.5}>
										<InputLabelWizard required>
											Ảnh bìa đại diện
										</InputLabelWizard>
										<Typography variant="caption" color="text.secondary">
											Hình ảnh đẹp sẽ giúp sự kiện của bạn thu hút thêm lượt
											truy cập từ trang tìm kiếm.
										</Typography>
									</Stack>
									<Stack mt={2} direction="row" alignItems="center">
										<Typography variant="caption" color="text.secondary">
											Kích thước chuẩn: <strong>1152 x 480 px</strong> (21:9)
										</Typography>

										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ mx: 1 }}
										>
											&#x2022;
										</Typography>

										<Typography variant="caption" color="text.secondary">
											File <strong>.jpg, .png</strong>
										</Typography>
									</Stack>

									<Field.Upload
										name="stepInfo.banner"
										maxSize={3145728}
										accept={{
											"image/jpeg": [],
											"image/jpg": [],
											"image/png": [],
										}}
										sx={{
											width: {
												xs: "100%",
												md: "80%",
											},
											mt: 0.5,
										}}
										placeholder={
											<Stack justifyContent="center" alignItems="center">
												<ExtraImageSvg />
												<Button
													startIcon={<Iconify icon="eva:cloud-upload-fill" />}
													variant="contained"
													color="primary"
												>
													Tải ảnh
												</Button>
											</Stack>
										}
										onDelete={() =>
											setValue("stepInfo.banner", null, {
												shouldValidate: true,
											})
										}
									/>

									<Typography variant="caption" color="text.secondary">
										Nội dung hiển thị tốt nhất nên nằm trong vùng an toàn:{" "}
										<strong>853 x 480 px.</strong>{" "}
										<Link
											className="underline"
											href={SUGGEST_BANNER_LINK}
											target="_blank"
										>
											Xem chi tiết
										</Link>
									</Typography>
								</Box>

								<Stack spacing={1}>
									<InputLabelWizard required>Nội dung sự kiện</InputLabelWizard>
									<Field.Editor
										name="stepInfo.content"
										placeholder="Nội dung sự kiện."
										sx={{
											["& .nml__editor__content__root"]: {
												py: 1.5,
											},
										}}
									/>
								</Stack>

								<RHFSelectCategoriesV2
									name="stepInfo.categories"
									label="Lĩnh vực"
									helperText="Vui lòng chọn tối đa 3 lĩnh vực"
									placeholder=""
									textFieldProps={{
										InputLabelProps: {
											shrink: undefined,
										},
										required: true,
									}}
									type={[CategoryType.EVENT]}
								/>

								<Field.Autocomplete
									name="stepInfo.hashtags"
									label="Hashtag"
									multiple
									helperText={
										<Stack
											direction="row"
											alignItems="center"
											spacing={1}
											ml={-1}
										>
											<span>
												Nhập tag bạn muốn thêm và nhấn <strong>Enter</strong>
											</span>
										</Stack>
									}
									freeSolo
									options={[]}
									renderTags={(selected, getTagProps) => {
										return selected.map((option: unknown, index: number) => {
											const { key, ...tagProps } = getTagProps({ index });

											return (
												<Chip
													key={key}
													{...tagProps}
													label={"#" + option}
													size="small"
													color="info"
													variant="soft"
												/>
											);
										});
									}}
								/>
							</Stack>
						</CardContent>
					</CardWizard>

					<CardWizard>
						<CardContent>
							<Stack spacing={2}>
								<Stack>
									<InputLabelWizard>Video</InputLabelWizard>
									<Typography variant="caption" color="text.secondary">
										Thêm liên kết video cho sự kiện (chỉ hỗ trợ{" "}
										<strong>Youtube, Facebook</strong>)
									</Typography>
								</Stack>
								<Stack spacing={2}>
									{fields.map((field, index) => {
										return (
											<Stack key={field.id} direction="row" spacing={2}>
												<Field.Text
													InputProps={{
														startAdornment: (
															<InputAdornment position="start">
																<Iconify
																	width={24}
																	color="rgba(24, 119, 242, 1)"
																	icon="ci:link"
																/>
															</InputAdornment>
														),
													}}
													sx={{
														flex: 1,
													}}
													name={`stepInfo.videos.${index}.url`}
													label="URL"
													placeholder="VD: https://www.youtube.com/watch?v=xxxx"
												/>
												<Box margin="auto">
													{index === fields.length - 1 ? (
														<IconButton
															onClick={() => append({ url: "" })}
															sx={{
																backgroundColor: "rgba(0, 80, 255, 0.08)",
															}}
															color="primary"
														>
															<Iconify width={24} icon="mingcute:add-line" />
														</IconButton>
													) : (
														<IconButton
															onClick={() => remove(index)}
															color="error"
														>
															<Iconify
																width={24}
																icon="solar:trash-bin-trash-bold"
															/>
														</IconButton>
													)}
												</Box>
											</Stack>
										);
									})}
								</Stack>
								<Stack mt={1}>
									<InputLabelWizard>Hình ảnh</InputLabelWizard>
									<Stack mt={2} direction="row" alignItems="center">
										<Typography variant="caption" color="text.secondary">
											Kích thước chuẩn: <strong>1280 x 720</strong> (16:9)
										</Typography>

										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ mx: 1 }}
										>
											&#x2022;
										</Typography>

										<Typography variant="caption" color="text.secondary">
											File <strong>.jpg, .png</strong>
										</Typography>

										<Typography
											variant="caption"
											color="text.secondary"
											sx={{ mx: 1 }}
										>
											&#x2022;
										</Typography>

										<Typography variant="caption" color="text.secondary">
											Tối đa 5 hình
										</Typography>
									</Stack>
									<Grid container spacing={1.5}>
										{photoFields.map((field, index) => {
											return (
												<Grid
													key={field.id}
													item
													lg={4}
													xs={6}
													sx={{ mt: 0.5 }}
												>
													<Field.Upload
														name={`stepInfo.photos.${index}.file`}
														maxSize={3145728}
														accept={{
															"image/jpeg": [],
															"image/jpg": [],
															"image/png": [],
														}}
														sx={{
															height: 125,
															maxHeight: 125,
															[`& > .${boxClasses.root}`]: {
																padding: "unset",
																height: "100%",
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
															},
														}}
														placeholder={
															<Stack
																justifyContent="center"
																alignItems="center"
															>
																<Iconify
																	color="text.secondary"
																	width={24}
																	icon="mdi:plus-circle-outline"
																/>
															</Stack>
														}
														onDrop={(acceptedFiles) => {
															const value = acceptedFiles[0];

															setValue(`stepInfo.photos.${index}.file`, value, {
																shouldValidate: true,
															});

															setValue(
																"stepInfo._recalculatePhotosAndVideos",
																recalculatePhotosAndVideos + 1,
																{
																	shouldValidate: true,
																},
															);
														}}
														onDelete={() => {
															if (photoFields.length > 3) {
																removePhoto(index);
																return;
															}

															setValue(`stepInfo.photos.${index}.file`, null, {
																shouldValidate: true,
															});

															setValue(
																"stepInfo._recalculatePhotosAndVideos",
																recalculatePhotosAndVideos + 1,
																{
																	shouldValidate: true,
																},
															);
														}}
													/>
												</Grid>
											);
										})}
									</Grid>
								</Stack>
							</Stack>
						</CardContent>
					</CardWizard>

					<CardWizard>
						<CardContent>
							<Stack spacing={2}>
								<Stack>
									<InputLabelWizard>Form khảo sát</InputLabelWizard>
								</Stack>
								<Stack spacing={2}>
									<RHFSelectSurvey
										name={"stepInfo.surveyIds"}
										requiredName={"stepInfo.surveyRequired"}
										fromProductId={pickSurveyFromProductId}
										disabledAdd={shouldGetSurveysFromProduct}
									/>
								</Stack>
							</Stack>
						</CardContent>
					</CardWizard>
				</Stack>
				{navigator}
			</Box>
			{!mdDown && <PreviewInfo data={data as WizardSchemaType} />}
		</Stack>
	);
};

export default StepInfo;
